<template>
  <div id="backdoor_epos_receipt">
    <div class="main">
      <div class="title_bar">
        <div class="left">
          <div class="title">
            出單機明細單測試
          </div>
          <div class="tip">
            僅 dev / staging 可用。列印走正式的 printThermalPrinter，預覽即為模板 V4 實際送印的點陣圖
          </div>
        </div>
      </div>

      <div class="layout">
        <div class="control_panel">
          <div class="section_title">
            1. 出單機
          </div>
          <el-select
            v-model="ip"
            class="ip_select"
            filterable
            allow-create
            default-first-option
            placeholder="選擇或輸入出單機 IP"
          >
            <el-option
              v-for="printerItem in communityPrinters"
              :key="printerItem.Ip"
              :label="`${printerItem.Name || '未命名'}（${printerItem.Ip}）`"
              :value="printerItem.Ip"
            />
          </el-select>
          <div class="button_row">
            <el-button
              size="small"
              @click="fetchCommunityPrinters"
            >
              帶入本社區出單機
            </el-button>
            <el-button
              size="small"
              :loading="isTesting"
              @click="testConnection"
            >
              連線測試
            </el-button>
          </div>

          <div class="section_title">
            2. 模板版本
          </div>
          <el-radio-group v-model="templateVersion">
            <el-radio :label="4">
              V4 新版（點陣圖）
            </el-radio>
            <el-radio :label="3">
              V3 舊版（文字指令）
            </el-radio>
          </el-radio-group>

          <div class="section_title">
            3. V4 字體樣式
          </div>
          <div class="style_field">
            <span class="style_label">字級倍率</span>
            <el-input-number
              v-model="receiptStyle.fontScale"
              size="small"
              :min="0.8"
              :max="1.4"
              :step="0.05"
              :precision="2"
            />
            <span class="style_hint">等比放大，不變形；每行字數變少</span>
          </div>
          <div class="style_field">
            <span class="style_label">垂直拉長倍率</span>
            <el-input-number
              v-model="receiptStyle.textScaleY"
              size="small"
              :min="1"
              :max="2"
              :step="0.1"
              :precision="1"
            />
            <span class="style_hint">只拉高不加寬；2 = V3 比例</span>
          </div>
          <div class="button_row">
            <el-button
              v-for="preset in stylePresets"
              :key="preset.label"
              size="small"
              @click="applyStyle(preset.style)"
            >
              {{ preset.label }}
            </el-button>
          </div>
          <div class="style_code">
            <div>選定後，將 src/js/utils/eposReceiptCanvas.js 的 DEFAULT_RECEIPT_STYLE 改為：</div>
            <code>fontScale: {{ receiptStyle.fontScale }}, textScaleY: {{ receiptStyle.textScaleY }}</code>
          </div>

          <div class="section_title">
            4. 測試資料
          </div>
          <div class="button_row">
            <el-button
              size="small"
              @click="applyPreset('design')"
            >
              設計稿資料
            </el-button>
            <el-button
              size="small"
              @click="applyPreset('extreme')"
            >
              極端資料
            </el-button>
            <el-checkbox
              :value="isVoidOrCancel"
              :disabled="!parsedData"
              @change="toggleVoidOrCancel"
            >
              拒單 / 作廢 / 取消
            </el-checkbox>
          </div>
          <el-input
            v-model="dataJson"
            class="data_json"
            type="textarea"
            :rows="16"
            spellcheck="false"
          />
          <div
            v-if="dataError"
            class="error_text"
          >
            {{ dataError }}
          </div>

          <div class="section_title">
            5. 列印
          </div>
          <el-button
            type="primary"
            :loading="isPrinting"
            :disabled="!parsedData"
            @click="print"
          >
            列印明細單
          </el-button>

          <div class="section_title">
            紀錄
          </div>
          <pre class="log_panel">{{ logs.join('\n') }}</pre>
        </div>

        <div class="preview_panel">
          <div class="section_title">
            預覽（模板 V4，即為實際送印的點陣圖）
          </div>
          <el-radio-group
            v-model="previewMode"
            size="small"
          >
            <el-radio-button label="physical">
              實體尺寸
            </el-radio-button>
            <el-radio-button label="dot">
              放大檢視（1 dot = 1 px）
            </el-radio-button>
          </el-radio-group>
          <div class="preview_info">
            {{ previewInfo }}
            <template v-if="previewMode === 'physical'">
              <br>實體尺寸以 58mm 紙、{{ printerDpi }} dpi 換算，實際大小依螢幕略有誤差，可拿實體單據放在螢幕旁比對
            </template>
          </div>
          <div
            class="paper"
            :class="{ is_physical: previewMode === 'physical' }"
          >
            <div
              ref="preview"
              class="paper_print_area"
              :style="printAreaStyle"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiPoint } from '@/api/point/point.js'
import { printThermalPrinter, testPrinterConnection } from '@/js/utils/epos.js'
import { drawReceiptCanvas, DEFAULT_RECEIPT_STYLE, RECEIPT_CANVAS_WIDTH, PRINTER_DPI } from '@/js/utils/eposReceiptCanvas.js'

const IP_STORAGE_KEY = 'backdoorEposReceipt.ip' // 記住上次使用的出單機 IP

// 測試資料，欄位同 PointConfirmation / OrderFoodManagement 組出的列印資料
const RECEIPT_PRESETS = {
  design: {
    comOrderId: 'T0923-0012', // 訂單編號
    dining: '內用 / 桌號 5', // 用餐方式
    userId: 'A1-3F-2', // 戶號
    items: [
      { name: '葡萄汁不能超過十字元', quantity: 12, price: 1665, displaySpec: '去冰/無糖/中杯/加珍珠' },
      { name: 'Pizza', quantity: 2, price: 50, displaySpec: '12寸/升級套餐' },
      { name: '蘋果汁不能超過十字元', quantity: 1, price: 20, displaySpec: '12寸/升級套餐' }
    ],
    isPointSupplement: true, // 是否啟用點數補足功能
    selectedPointType: 1, // 選擇的交易點數類別
    totalPoint: 20100, // 交易點數合計
    normalPointBefore: 20100, // 一般點數(交易前)
    cashPointBefore: 0, // 現金點數(交易前)
    normalPointAfter: 0, // 一般點數(交易後)
    cashPointAfter: 0, // 現金點數(交易後)
    note: '請少冰，謝謝', // 備註
    date: '2026-09-24 14:30:00', // 時間
    isVoidOrCancel: false // 是否為「拒單/作廢/取消」明細單
  },
  extreme: {
    comOrderId: 'T0924-9999',
    dining: '外帶',
    userId: 'B12-15F-1503',
    items: [
      { name: 'Iced Caramel Macchiato Venti', quantity: 100, price: 150, displaySpec: '少冰/半糖/大杯/加燕麥奶/加濃縮/外帶提袋/不要吸管' },
      { name: '超長品名測試超長品名測試超長品名', quantity: 3, price: 33333, displaySpec: '' },
      { name: '湯', quantity: 1, price: 1, displaySpec: 'A' }
    ],
    isPointSupplement: false,
    selectedPointType: 2,
    totalPoint: 115000,
    normalPointBefore: 0,
    cashPointBefore: 200000,
    normalPointAfter: 0,
    cashPointAfter: 85000,
    note: '顧客來電取消，餐點尚未製作，已通知廚房停止備餐並退還點數至原帳戶',
    date: '2026-09-24 23:59:59',
    isVoidOrCancel: false
  }
}

// 字體樣式快速組合
const STYLE_PRESETS = [
  { label: '目前預設', style: { ...DEFAULT_RECEIPT_STYLE } },
  { label: '同 V3 比例', style: { fontScale: 1, textScaleY: 2 } },
  { label: '等比放大', style: { fontScale: 1.25, textScaleY: 1 } },
  { label: '折衷', style: { fontScale: 1.15, textScaleY: 1.4 } }
]

export default {
  name: 'BackdoorEposReceipt',
  data () {
    return {
      ip: '', // 出單機 IP
      communityPrinters: [], // 本社區出單機列表
      templateVersion: 4, // 模板版本
      dataJson: '', // 測試資料 JSON 字串
      dataError: '', // 測試資料或預覽錯誤訊息
      previewInfo: '', // 預覽尺寸資訊
      logs: [], // 紀錄（新的在上）
      receiptStyle: { ...DEFAULT_RECEIPT_STYLE }, // V4 字體樣式參數
      stylePresets: STYLE_PRESETS, // 字體樣式快速組合
      previewMode: 'physical', // 預覽模式：physical 實體尺寸、dot 放大檢視
      printerDpi: PRINTER_DPI, // 出單機解析度
      isTesting: false, // 是否正在連線測試
      isPrinting: false // 是否正在列印
    }
  },
  computed: {
    /** 解析後的測試資料，JSON 不合法時為 null */
    parsedData () {
      try {
        return JSON.parse(this.dataJson)
      } catch (e) {
        return null
      }
    },
    isVoidOrCancel () {
      return !!(this.parsedData && this.parsedData.isVoidOrCancel)
    },
    /** 預覽列印範圍寬度：實體尺寸依 dpi 換算成 mm；放大檢視則 1 dot = 1 px */
    printAreaStyle () {
      const width = this.previewMode === 'physical'
        ? `${(RECEIPT_CANVAS_WIDTH / PRINTER_DPI * 25.4).toFixed(2)}mm`
        : `${RECEIPT_CANVAS_WIDTH}px`
      return { width }
    }
  },
  watch: {
    dataJson () {
      this.schedulePreview()
    },
    receiptStyle: {
      handler () {
        this.schedulePreview()
      },
      deep: true
    },
    ip (value) {
      try {
        localStorage.setItem(IP_STORAGE_KEY, value)
      } catch (e) {
        // 無痕模式等情況無法寫入，忽略
      }
    }
  },
  created () {
    this.previewTimer = null // 預覽 debounce 計時器
    try {
      this.ip = localStorage.getItem(IP_STORAGE_KEY) || ''
    } catch (e) {
      // 無痕模式等情況無法讀取，忽略
    }
    this.applyPreset('design')
  },
  beforeDestroy () {
    clearTimeout(this.previewTimer)
  },
  methods: {
    addLog (message) {
      this.logs.unshift(`${dayjs().format('HH:mm:ss')}  ${message}`)
    },
    applyStyle (style) {
      this.receiptStyle = { ...style }
    },
    applyPreset (key) {
      this.dataJson = JSON.stringify(RECEIPT_PRESETS[key], null, 2)
    },
    toggleVoidOrCancel (checked) {
      if (!this.parsedData) return
      this.dataJson = JSON.stringify({ ...this.parsedData, isVoidOrCancel: checked }, null, 2)
    },
    schedulePreview () {
      clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(this.renderPreview, 300)
    },
    /** 以正式的繪製模組產生預覽，內容與送印的點陣圖相同 */
    async renderPreview () {
      if (!this.parsedData) {
        this.dataError = '測試資料不是合法的 JSON'
        return
      }
      this.dataError = ''
      try {
        const canvas = await drawReceiptCanvas(this.parsedData, this.receiptStyle)
        if (!this.$refs.preview) return
        this.$refs.preview.replaceChildren(canvas)
        const lengthMm = Math.round(canvas.height / PRINTER_DPI * 25.4)
        this.previewInfo = `${canvas.width} × ${canvas.height} dots，單據長約 ${lengthMm} mm，點陣資料約 ${Math.round(canvas.width * canvas.height / 8 / 1024)} KB`
      } catch (e) {
        this.dataError = `預覽失敗：${e.message}`
      }
    },
    async fetchCommunityPrinters () {
      try {
        const res = await apiPoint.getCommunityPrinters()
        this.communityPrinters = res.data.Data.Printers || []
        this.addLog(`本社區出單機共 ${this.communityPrinters.length} 台`)
        if (!this.ip && this.communityPrinters.length) this.ip = this.communityPrinters[0].Ip
      } catch (e) {
        this.addLog(`取得本社區出單機失敗：${e.message}`)
      }
    },
    async testConnection () {
      const ip = this.ip.trim()
      if (!ip) {
        this.addLog('請先選擇或輸入出單機 IP')
        return
      }
      this.isTesting = true
      try {
        const res = await testPrinterConnection(ip, 1)
        this.addLog(`連線測試 ${ip}：${res}`)
      } finally {
        this.isTesting = false
      }
    },
    async print () {
      const ip = this.ip.trim()
      if (!ip) {
        this.addLog('請先選擇或輸入出單機 IP')
        return
      }
      const templateVersion = this.templateVersion
      const receiptStyle = { ...this.receiptStyle }
      const label = `[V${templateVersion}]`
      const startAt = performance.now()
      this.isPrinting = true
      const styleText = templateVersion === 4 ? `（fontScale: ${receiptStyle.fontScale}, textScaleY: ${receiptStyle.textScaleY}）` : ''
      this.addLog(`${label} 送出至 ${ip}${styleText}`)
      try {
        await printThermalPrinter(templateVersion, ip, _cloneDeep(this.parsedData), undefined, {
          receiptStyle,
          onReceive: (res) => {
            const elapsed = Math.round(performance.now() - startAt)
            this.addLog(`${label} 出單機回應 success=${res.success} code=${res.code} status=${res.status}，耗時 ${elapsed} ms`)
          }
        })
        this.addLog(`${label} 指令已送出，等待出單機回應`)
      } catch (e) {
        this.addLog(`${label} 列印失敗：${e && e.message ? e.message : e}`)
      } finally {
        this.isPrinting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#backdoor_epos_receipt {
  @include mainLayer();
  .title {
    font-weight: bold;
    font-size: 25px;
    margin-bottom: 10px;
    text-align: left;
  }
  .tip {
    text-align: left;
    color: #777;
  }
  .layout {
    display: flex;
    gap: 32px;
    align-items: flex-start;
    margin-top: 20px;
    text-align: left;
  }
  .control_panel {
    flex: 0 0 420px;
  }
  .section_title {
    font-weight: bold;
    margin: 20px 0 8px;
    padding-left: 8px;
    border-left: 4px solid #333;
  }
  .ip_select {
    width: 100%;
  }
  .button_row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  .data_json ::v-deep textarea {
    font-family: Menlo, Consolas, monospace;
    font-size: 12px;
  }
  .error_text {
    color: #d93025;
    margin-top: 4px;
  }
  .log_panel {
    height: 220px;
    overflow: auto;
    margin: 0;
    padding: 8px;
    background: #1e1e1e;
    color: #9fe39f;
    font-size: 12px;
    white-space: pre-wrap;
  }
  .preview_info {
    color: #777;
    font-size: 12px;
    margin-bottom: 8px;
  }
  .style_field {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 6px 0;
  }
  .style_label {
    flex: 0 0 90px;
  }
  .style_hint {
    color: #777;
    font-size: 12px;
  }
  .style_code {
    margin-top: 6px;
    padding: 8px;
    background: #f5f5f5;
    font-size: 12px;
    color: #555;
    code {
      display: block;
      margin-top: 4px;
      color: #222;
      font-family: Menlo, Consolas, monospace;
    }
  }
  .paper {
    display: inline-block;
    margin-top: 8px;
    padding: 12px 0;
    background: #fff;
    border: 1px solid #ddd;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    // 實體尺寸：58mm 紙寬，可列印範圍置中
    &.is_physical {
      width: 58mm;
      box-sizing: border-box;
      .paper_print_area {
        margin: 0 auto;
      }
    }
    ::v-deep canvas {
      display: block;
      width: 100%;
      image-rendering: pixelated;
    }
  }
}
</style>
